<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto focus:outline-none" tabindex="0">
        <form @submit.prevent="saveFile">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('Datei bearbeiten') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div ref="headerActionMenuWrapper" class="relative inline-flex items-stretch mr-0">
                            <button class="btn-stage-primary !rounded-r-none" type="submit">
                                <span>{{ $t('Datei speichern') }}</span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.12)] min-w-[2.5rem] !px-3 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Datei löschen') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel header-class="!p-0" body-class="!p-0" footer-class="px-6 py-4 text-xs text-gray-500">
                        <template #header>
                            <div class="flex min-h-[57px] w-full items-stretch justify-between">
                                <div class="min-w-0 flex-1 truncate px-6 py-4 text-sm font-medium text-gray-700">
                                    {{ file.url || '—' }}
                                </div>
                                <div class="flex w-14 shrink-0 items-stretch border-l border-ultralight">
                                    <button
                                        class="flex h-full w-full cursor-pointer items-center justify-center text-gray-700 hover:text-gray-900"
                                        type="button"
                                        :title="$t('URL kopieren')"
                                        :aria-label="$t('URL kopieren')"
                                        @click="copyUrl"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.clipboard"></svg-vue>
                                    </button>
                                </div>
                            </div>
                        </template>
                            <div class="flex flex-col xl:flex-row">
                                <div class="min-w-0 flex-1">
                                    <div class="p-6">
                                        <div class="space-y-6">
                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Dateiname') }}</label>
                                            <input v-model="file.name" class="stage-filter-input form-input block w-full text-sm" required>
                                            <div class="mt-2 text-xs text-gray-500">
                                                {{ $t('Original beim Upload:') }} {{ file.original_name || '-' }}
                                            </div>
                                        </div>

                                        <div class="border border-bordergray-100 rounded p-3">
                                            <input ref="replaceInput" hidden type="file" @change="onReplaceFileSelected($event)">
                                            <div class="flex items-center justify-between gap-3 w-full">
                                                <button class="btn-stage-secondary" type="button" @click="selectReplaceFile">
                                                    {{ $t('Datei ersetzen') }}
                                                </button>
                                                <span v-if="replacementFileName" class="text-xs text-gray-500 truncate">
                                                    {{ $t('Ausgewählt:') }} {{ replacementFileName }}
                                                </span>
                                                <span v-else class="text-xs text-gray-500 truncate">
                                                    {{ $t('Keine Ersatzdatei ausgewählt') }}
                                                </span>
                                            </div>
                                            <div class="mt-3 text-xs text-gray-500">
                                                {{ $t('Die Ersatzdatei muss dasselbe Dateiformat besitzen, wie die initial hochgeladenen Datei.') }}
                                            </div>
                                        </div>

                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Kategorie') }}</label>
                                            <select v-model="file.category_id" class="stage-filter-input form-select block w-full text-sm">
                                                <option :value="null">{{ $t('Keine Kategorie') }}</option>
                                                <option v-for="category in categories" :key="'edit-category-' + category.id" :value="Number(category.id)">
                                                    {{ category.name }}
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label class="stage-filter-label block mb-2">{{ $t('Kurzbeschreibung') }}</label>
                                            <textarea v-model="file.short_description" class="stage-filter-input form-textarea block w-full text-sm min-h-[8rem]"></textarea>
                                        </div>
                                        </div>
                                    </div>

                                    <div class="overflow-hidden border-t border-line-subtle">
                                        <button
                                            class="flex w-full cursor-pointer items-center justify-between gap-4 border-0 border-b border-line-subtle bg-surface-raised px-6 py-4 text-left text-xs font-medium uppercase tracking-[0.05em] text-ink-muted"
                                            type="button"
                                            @click="metaTagsAccordionOpen = !metaTagsAccordionOpen"
                                        >
                                            <span>{{ $t('Metatags') }}</span>
                                            <svg-vue
                                                class="h-[0.875rem] w-[0.875rem] text-gray-400"
                                                :class="metaTagsAccordionOpen ? 'rotate-180' : ''"
                                                icon="font-awesome.regular.angle-down"
                                            ></svg-vue>
                                        </button>
                                        <div v-if="metaTagsAccordionOpen" class="bg-white">
                                            <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 px-6 pt-4">
                                                <button
                                                    v-for="(language, index) in languages"
                                                    :key="'file-meta-language-' + language.id"
                                                    type="button"
                                                    class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                                    :class="activeTranslationIndex === index ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                                    @click="activeTranslationIndex = index"
                                                >
                                                    {{ language.locale || language.name }}
                                                </button>
                                            </div>
                                            <div class="p-6">
                                                <div v-if="activeMetaTranslation" class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('Alt-Text') }}</label>
                                                        <input v-model="activeMetaTranslation.alt_text" class="stage-filter-input form-input block w-full text-sm">
                                                    </div>
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('Titel-Text') }}</label>
                                                        <input v-model="activeMetaTranslation.title_text" class="stage-filter-input form-input block w-full text-sm">
                                                    </div>
                                                    <div class="xl:col-span-2">
                                                        <label class="stage-filter-label block mb-2">{{ $t('Bildunterschrift') }}</label>
                                                        <textarea v-model="activeMetaTranslation.caption" class="stage-filter-input form-textarea block w-full text-sm min-h-[6rem]"></textarea>
                                                    </div>
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('Quelle') }}</label>
                                                        <input v-model="activeMetaTranslation.source_reference" class="stage-filter-input form-input block w-full text-sm">
                                                    </div>
                                                    <div>
                                                        <label class="stage-filter-label block mb-2">{{ $t('Copyright') }}</label>
                                                        <input v-model="activeMetaTranslation.copyright_notice" class="stage-filter-input form-input block w-full text-sm">
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="px-6 py-4">
                                        <label class="inline-flex cursor-pointer items-center text-sm text-gray-700">
                                            <input v-model="file.is_decorative" type="checkbox" class="form-checkbox h-4 w-4 mr-3 text-gray-700">
                                            <span>{{ $t('Dekoratives Bild ohne semantischen Inhalt') }}</span>
                                        </label>
                                    </div>
                                </div>
                                <aside class="w-full shrink-0 border-t border-ultralight bg-white xl:w-[21rem] xl:border-t-0 xl:border-l">
                                    <div>
                                        <div class="p-6">
                                            <div class="relative aspect-square overflow-hidden rounded bg-gray-50 flex items-center justify-center">
                                                <button
                                                    v-if="canRenderImage(file)"
                                                    type="button"
                                                    class="absolute top-3 right-3 z-10 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-white/50 text-gray-700 shadow-sm hover:bg-white/70"
                                                    :title="$t('Vorschau öffnen')"
                                                    :aria-label="$t('Vorschau öffnen')"
                                                    @click="openImagePreview"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.eye"></svg-vue>
                                                </button>
                                                <img v-if="canRenderImage(file)" :src="file.url" :alt="file.name" class="h-full w-full object-contain">
                                                <svg-vue v-else class="h-16 w-16 text-gray-500" :icon="fileIcon(file.extension)"></svg-vue>
                                            </div>
                                        </div>
                                        <div class="border-t border-ultralight"></div>
                                        <div class="p-6">
                                            <div class="space-y-2 text-sm text-gray-500">
                                                <div><span class="text-gray-700">{{ $t('Typ:') }}</span> {{ (file.extension || '-').toUpperCase() }}</div>
                                                <div><span class="text-gray-700">MIME:</span> {{ file.mime_type || '-' }}</div>
                                                <div><span class="text-gray-700">{{ $t('Größe:') }}</span> {{ file.size_readable || '-' }}</div>
                                                <div v-if="file.width || file.height"><span class="text-gray-700">{{ $t('Abmessungen:') }}</span> {{ file.width || '–' }} × {{ file.height || '–' }} px</div>
                                                <div><span class="text-gray-700">{{ $t('Upload:') }}</span> {{ uploadedAtLabel }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        <template #footer>
                            <div class="w-full break-all">
                                {{ $t('Lokaler Pfad:') }} {{ file.local_storage_path || '—' }}
                            </div>
                        </template>
                    </app-panel>
                </div>
            </div>
        </form>

        <confirm-dialog :open="deleteModal" :title="$t('Datei löschen').toString()" :text="$t('Soll diese CMS-Datei wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.').toString()" :confirm-label="$t('Löschen').toString()" confirm-variant="danger" @close="deleteModal = false" @confirm="deleteFile" />

        <div v-show="previewModal.open" class="app-dialog-root fixed z-50 inset-0 overflow-y-auto" @click.self="closeImagePreview">
            <div class="flex items-center justify-center min-h-screen px-4 py-8">
                <div class="app-dialog-overlay fixed inset-0 z-40 transition-opacity"><div class="absolute inset-0 bg-black opacity-80"></div></div>
                <div class="relative z-50 max-w-6xl w-full flex items-center justify-center">
                    <div class="relative inline-block max-w-full">
                        <button
                            type="button"
                            class="absolute -top-9 right-0 z-10 inline-flex items-center justify-center w-8 h-8 p-0 bg-transparent border-0 text-white leading-none focus:outline-none"
                            @click="closeImagePreview"
                        >
                            <svg viewBox="0 0 24 24" fill="none" class="h-6 w-6">
                                <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <img :src="previewModal.url" :alt="previewModal.title || $t('Dateivorschau')" class="block max-h-[85vh] w-auto max-w-full object-contain">
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {isCmsImageFileExtension, resolveCmsFileIcon} from "@/utils/cms-file-manager";
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: "cms-file-manager-edit",
    metaInfo() {
        return {
            title: this.$t('Datei bearbeiten')
        }
    },
    data() {
        return {
            loading: false,
            deleteModal: false,
            headerActionMenuOpen: false,
            metaTagsAccordionOpen: false,
            activeTranslationIndex: 0,
            file: {
                uuid: null,
                name: '',
                original_name: '',
                public_path: '',
                short_description: '',
                translations: [],
                is_decorative: false,
                category_id: null,
                url: '',
                extension: '',
                mime_type: '',
                size: 0,
                size_readable: '',
                width: null,
                height: null,
                created_at: null,
            },
            categories: [],
            languages: [],
            replacementFile: null,
            replacementFileName: '',
            previewModal: {
                open: false,
                url: '',
                title: '',
            },
        };
    },
    computed: {
        fileManagerListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/cms/file-manager');
        },
        activeMetaTranslation() {
            return this.file.translations[this.activeTranslationIndex] || null;
        },
        uploadedAtLabel() {
            if (!this.file.created_at) {
                return '-';
            }

            return moment(this.file.created_at).format('DD.MM.YYYY HH:mm');
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        this.loadOptions();
        this.loadFile();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        loadOptions() {
            axios.get('api/workspace/admin/cms/file-manager/options').then((response) => {
                this.categories = response.data.categories || [];
                this.languages = response.data.languages || [];
                this.ensureTranslationEntries();
            });
        },
        loadFile() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/file-manager/' + this.$route.params.uuid).then((response) => {
                this.languages = response.data.languages || this.languages;
                this.file = this.normalizeFilePayload(response.data);
                this.setDefaultTranslationTab();
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        saveFile() {
            const formData = new FormData();
            formData.append('name', this.file.name || '');
            formData.append('short_description', this.file.short_description || '');
            formData.append('is_decorative', this.file.is_decorative ? '1' : '0');
            if (this.file.category_id) {
                formData.append('category_id', String(this.file.category_id));
            }
            this.file.translations.forEach((translation, index) => {
                formData.append(`translations[${index}][cms_language_id]`, String(translation.cms_language_id));
                formData.append(`translations[${index}][alt_text]`, translation.alt_text || '');
                formData.append(`translations[${index}][title_text]`, translation.title_text || '');
                formData.append(`translations[${index}][caption]`, translation.caption || '');
                formData.append(`translations[${index}][copyright_notice]`, translation.copyright_notice || '');
                formData.append(`translations[${index}][source_reference]`, translation.source_reference || '');
            });
            if (this.replacementFile instanceof File) {
                formData.append('replacement_file', this.replacementFile);
            }
            formData.append('_method', 'PUT');

            this.loading = true;
            axios.post('api/workspace/admin/cms/file-manager/' + this.$route.params.uuid, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then((response) => {
                this.languages = response.data.languages || this.languages;
                this.file = this.normalizeFilePayload(response.data.file);
                this.setDefaultTranslationTab();
                this.clearReplacementFileSelection();
                this.loading = false;
                this.$notify({title: this.$t('Success'), text: this.$t(response.data.message || 'Datei erfolgreich gespeichert.'), type: 'success'});
            }).catch((error) => {
                this.loading = false;
                this.$notify({
                    title: this.$t('Error'),
                    text: error?.response?.data?.message || 'Datei konnte nicht gespeichert werden.',
                    type: 'error'
                });
            });
        },
        selectReplaceFile() {
            this.$refs.replaceInput.click();
        },
        replacementFileFormatHint() {
            return 'Die Ersatzdatei muss dasselbe Dateiformat besitzen, wie die initial hochgeladenen Datei.';
        },
        getFileExtension(fileName) {
            const normalized = String(fileName || '').trim();
            const segments = normalized.split('.');
            if (segments.length < 2) {
                return '';
            }

            return String(segments.pop() || '').toLowerCase();
        },
        clearReplacementFileSelection() {
            this.replacementFile = null;
            this.replacementFileName = '';
            if (this.$refs.replaceInput) {
                this.$refs.replaceInput.value = null;
            }
        },
        onReplaceFileSelected(event) {
            const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
            if (!file) {
                this.clearReplacementFileSelection();
                return;
            }

            const originalExtension = this.getFileExtension(this.file.original_name || this.file.name || '');
            const storedExtension = String(this.file.extension || '').toLowerCase();
            const expectedExtension = storedExtension || originalExtension;
            const replacementExtension = this.getFileExtension(file.name);

            if (expectedExtension && replacementExtension !== expectedExtension) {
                this.clearReplacementFileSelection();
                this.$notify({
                    title: this.$t('Error'),
                    text: this.replacementFileFormatHint(),
                    type: 'error'
                });
                return;
            }

            this.replacementFile = file;
            this.replacementFileName = file.name;
        },
        normalizeFilePayload(payload = {}) {
            const {languages, ...filePayload} = payload || {};
            const normalized = {
                ...filePayload,
                category_id: filePayload.category_id ? Number(filePayload.category_id) : null,
                size_readable: this.formatBytes(filePayload.size || 0),
                translations: Array.isArray(filePayload.translations) ? filePayload.translations.map((translation) => ({
                    cms_language_id: Number(translation.cms_language_id),
                    locale: translation.locale || '',
                    language_name: translation.language_name || '',
                    alt_text: translation.alt_text || '',
                    title_text: translation.title_text || '',
                    caption: translation.caption || '',
                    copyright_notice: translation.copyright_notice || '',
                    source_reference: translation.source_reference || '',
                })) : [],
            };

            this.ensureTranslationEntries(normalized);

            return normalized;
        },
        ensureTranslationEntries(targetFile = this.file) {
            if (!targetFile || !Array.isArray(this.languages) || this.languages.length === 0) {
                return;
            }

            const byLanguageId = new Map(
                (targetFile.translations || []).map((translation) => [Number(translation.cms_language_id), translation])
            );

            targetFile.translations = this.languages.map((language) => {
                const existing = byLanguageId.get(Number(language.id));

                return existing || {
                    cms_language_id: Number(language.id),
                    locale: language.locale || '',
                    language_name: language.name || '',
                    alt_text: '',
                    title_text: '',
                    caption: '',
                    copyright_notice: '',
                    source_reference: '',
                };
            });
        },
        setDefaultTranslationTab() {
            const defaultIndex = this.languages.findIndex((language) => Boolean(language.is_default));
            this.activeTranslationIndex = defaultIndex >= 0 ? defaultIndex : 0;
        },
        async copyUrl() {
            const text = String(this.file.url || '').trim();
            if (text === '') {
                return;
            }

            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const input = document.createElement('input');
                    input.value = text;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }

                this.$notify({title: this.$t('Success'), text: 'Öffentliche URL kopiert.', type: 'success'});
            } catch (error) {
                this.$notify({title: this.$t('Error'), text: 'Die URL konnte nicht kopiert werden.', type: 'error'});
            }
        },
        openImagePreview() {
            if (!this.canRenderImage(this.file)) {
                return;
            }

            this.previewModal = {
                open: true,
                url: this.file.url,
                title: this.file.name || 'Dateivorschau',
            };
        },
        closeImagePreview() {
            this.previewModal = {
                open: false,
                url: '',
                title: '',
            };
        },
        canRenderImage(file) {
            return !!file && this.isImageFile(file.extension) && file.file_exists !== false && !!file.url;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();
            if (action === 'cancel') {
                this.$router.push(this.fileManagerListPath);
                return;
            }
            if (action === 'delete') {
                this.deleteModal = true;
            }
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;
            const clickedInsideMenu = menuElement && menuElement.contains(event.target);
            const clickedTrigger = triggerElement && triggerElement.contains(event.target);
            const clickedWrapper = wrapperElement && wrapperElement.contains(event.target);

            if (!clickedInsideMenu && !clickedTrigger && !clickedWrapper) {
                this.closeHeaderActionMenu();
            }
        },
        deleteFile() {
            this.loading = true;
            axios.delete('api/workspace/admin/cms/file-manager/' + this.$route.params.uuid).then(() => {
                this.loading = false;
                this.$notify({title: this.$t('Success'), text: 'Datei gelöscht.', type: 'success'});
                this.$router.push(this.fileManagerListPath);
            }).catch(() => {
                this.loading = false;
                this.deleteModal = false;
            });
        },
        isImageFile: isCmsImageFileExtension,
        fileIcon: resolveCmsFileIcon,
        formatBytes(size) {
            const normalized = Number(size || 0);
            if (normalized < 1024) {
                return `${normalized} B`;
            }

            const units = ['KB', 'MB', 'GB', 'TB'];
            const base = Math.log(normalized) / Math.log(1024);
            const pow = Math.max(1, Math.min(Math.floor(base), units.length));
            const value = normalized / (1024 ** pow);

            return `${value >= 10 ? value.toFixed(0) : value.toFixed(1).replace('.', ',')} ${units[pow - 1]}`;
        },
    },
}
</script>
